import { Box } from "@chakra-ui/layout";
import _ from "lodash";
import { RefCallback, useCallback, useState } from "react";

type LazyRenderProps = {
  children: () => React.ReactNode;
  placeholder?: () => React.ReactNode;
};
export const LazyRender: React.FC<LazyRenderProps> = ({
  children,
  placeholder,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const sentinelRef = useCallback<RefCallback<HTMLDivElement>>((element) => {
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
        }
      },
      { threshold: 0 }
    );
    observer.observe(element);
  }, []);

  return shouldRender ? (
    children()
  ) : (
    <Box ref={sentinelRef}>{placeholder?.()}</Box>
  );
};

type LazyListProps<T> = {
  elements: T[];
  chunkSize?: number;
  children: (chunk: T[]) => React.ReactNode;
  placeholder: () => React.ReactNode;
};
export function LazyList<T>({
  elements,
  chunkSize = 1,
  children,
  placeholder,
}: LazyListProps<T>) {
  // Each chunk tracks its own render state, which avoids re-rendering the
  // entire list each time one is scrolled into view.
  return _.chunk(elements, chunkSize).map((chunk, index) => (
    <LazyRender key={index} placeholder={placeholder}>
      {() => children(chunk)}
    </LazyRender>
  ));
}
